import React, { useState } from 'react';
import { ArrowRight, Mail, Building, User, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { submitDemoRequest } from '../api/emboscale';

const CTASection = () => {
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmittingDemo, setIsSubmittingDemo] = useState(false);

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingDemo(true);

    try {
      const result = await submitDemoRequest(demoForm);
      
      toast.success(result.message, {
        duration: 5000,
      });

      // Reset form
      setDemoForm({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit demo request. Please try again.';
      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsSubmittingDemo(false);
    }
  };

  return (
    <section id="cta" className="py-32 bg-gradient-to-b from-white via-gray-100 to-white dark:from-black dark:via-gray-950 dark:to-black relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-emerald-600 dark:text-emerald-400 mb-4">07 — Get started</div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-gray-900 dark:text-white mb-5">
            Put the Factory Agent <span className="bg-gradient-to-r from-emerald-500 to-cyan-500 dark:from-emerald-400 dark:to-cyan-400 bg-clip-text text-transparent">on your line</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Bring a line you're planning — we'll build the twin, simulate it, and program a cell in front of you.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Demo Request Form */}
          <Card className="bg-white/80 dark:bg-gradient-to-br dark:from-gray-900/80 dark:to-black/80 backdrop-blur-md border border-black/10 dark:border-white/10">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Book a demo
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Tell us about your line and we'll get back to you within a day to set up a live session.
              </p>

              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <Input
                      type="text"
                      placeholder="Full Name"
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                      required
                      className="pl-10 bg-white dark:bg-black/50 border-black/20 dark:border-white/20 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-emerald-500 transition-colors duration-200"
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <Input
                      type="email"
                      placeholder="Work Email"
                      value={demoForm.email}
                      onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      required
                      className="pl-10 bg-white dark:bg-black/50 border-black/20 dark:border-white/20 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-emerald-500 transition-colors duration-200"
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <Input
                      type="text"
                      placeholder="Company Name"
                      value={demoForm.company}
                      onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                      required
                      className="pl-10 bg-white dark:bg-black/50 border-black/20 dark:border-white/20 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-emerald-500 transition-colors duration-200"
                    />
                  </div>
                </div>

                <div>
                  <Textarea
                    placeholder="Tell us about your use case (optional)"
                    value={demoForm.message}
                    onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })}
                    className="bg-white dark:bg-black/50 border-black/20 dark:border-white/20 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-emerald-500 transition-colors duration-200 min-h-[100px]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmittingDemo}
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white transition-colors duration-200 group"
                >
                  {isSubmittingDemo ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Book a demo
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

        </div>
      </div>
    </section>
  );
};

export default CTASection;
